import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import api from 'state/axios';
import { addDays, compareAsc } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import { keyframes } from "@mui/system";
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  ToggleButton,
} from '@mui/material';
import {
  Visibility,
  Warning,
  ToggleOn,
  ToggleOff,
} from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { setCrewRoutes } from "state";
import { fetchCrewRoutes } from 'state/api';
import { filterCrewRoutes } from 'state/helpers';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import ExportButton from 'components/ExportButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import NumberControl from 'components/NumberControl';
import CheckResultDialog from 'components/CheckResultDialog';
import RouteFilterDialog from 'components/RouteFilterDialog';
import RouteCrewListDialog from 'components/RouteCrewListDialog';
import RouteList from "./RouteList";


/* ── Module-level constants ────────────────────────────────────────── */
const checkTypes = [
  { value: "",           label: "None"       },
  { value: "gender",     label: "Gender"     },
  { value: "tat",        label: "TAT"        },
  { value: "training",   label: "Training"   },
  { value: "crewCount",  label: "Crew Count" },
];

const DELAY_THRESHOLDS = {
  HIGH:   { minutes: 60, color: '#f2292d' },
  MEDIUM: { minutes: 30, color: '#f29129' },
  LOW:    { minutes: 15, color: '#d7f229' },
};

const MIN_CREW_COUNT = 4;

const TRAINER_TO_TRAINEE = { L: 'l', C: 'c', U: 'u', B: 'b', A: 'a' };
const TRAINEE_TO_TRAINER = { l: 'L', c: 'C', u: 'U', b: 'B', a: 'A' };

const blink = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.8; }
  100% { opacity: 1; }
`;

const buildOptions = (data, key) =>
  [...new Set(data.map(item => item[key]))].sort();

const fmtCrewComplement = (items) => {
  if (!Array.isArray(items) || !items.length) return '';
  return items
    .map(({ rankCode, quantity, isCompulsory, actual, isMet }) =>
      `${rankCode}×${quantity}${isCompulsory ? '*' : ''} ${isMet ? '✓' : `✗ ${actual ?? 0}/${quantity}`}`
    )
    .join('\n');
};



/* ── Component ─────────────────────────────────────────────────────── */
const CrewRoutes = () => {
  useAuthCheck();
  const dispatch  = useDispatch();
  const user      = useSelector((state) => state.user);
  const timeMode  = useSelector((state) => state.timemode);
  const params    = useSelector((state) => state.params);
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));

  const isAdmin  = user.role === 'admin';
  const canView  = hasPermission(user?.permissions, "CrewRoutes", "view",  false);
  const canCheck = hasPermission(user?.permissions, "CrewRoutes", "check", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [switched, setSwitched] = useState(true);

  const [checksList,             setChecksList]             = useState([]);
  const [showCheckResultDialog,  setShowCheckResultDialog]  = useState(false);
  const [isFilterDialogOpen,     setIsFilterDialogOpen]     = useState(false);
  const [filters,                setFilters]                = useState(null);
  const [openRouteCrewListDialog, setOpenRouteCrewListDialog] = useState(false);
  const [crewList,  setCrewList]  = useState([]);
  const [crewRoute, setCrewRoute] = useState([]);

  const crewRanksData  = useSelector((state) => state.crewRanks);
  const crewRoutesData = useSelector((state) => state.crewRoutes);

  const [checkType,         setCheckType]         = useState('');
  const [acTypes,           setACTypes]           = useState([]);
  const [aircrafts,         setAircrafts]         = useState([]);
  const [crewComplements,   setCrewComplements]   = useState([]);
  const [minFemaleQuantity, setMinFemaleQuantity] = useState(1);
  const [maxFemaleQuantity, setMaxFemaleQuantity] = useState(99);

  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );
  const minCrewRouteTAT = Number(paramsMap.get("MINIMUM_CREW_ROUTE_TAT_MINUTES")) || 80;

  /* ── Data fetching ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!startDate || !endDate) return;
    const fetchData = async () => {
      setError(null);
      setLoading(true);
      try {
        const [
          acTypesRes, 
          aircraftsRes, 
          complementsRes
        ] = await Promise.all([
          api.get('/actypes/active'),
          api.get('/aircrafts/active'),
          api.get('/crewcomplements'),
        ]);

        setACTypes(acTypesRes.data || []);
        setAircrafts(aircraftsRes.data || []);
        setCrewComplements(complementsRes.data || []);

        if (switched) {
          const ret = await fetchCrewRoutes(
            addDays(startDate, -1), 
            addDays(endDate, 1)
          );

          dispatch(setCrewRoutes({
            crewRoutes: ret.filter(r => {
              const d = timeMode === 'UTC' ? r.routeDate : r.routeDateLocalBase;
              return compareAsc(d, startDate) >= 0 && compareAsc(d, endDate) <= 0;
            }),
          }));
        } else {
          const res = await api.post('/crewroutes', {
            startDate: addDays(startDate, -1),
            endDate:   addDays(endDate, 1),
          });
          dispatch(setCrewRoutes({
            crewRoutes: res.data.filter(r =>
              compareAsc(r.routeDate, startDate) >= 0 &&
              compareAsc(r.routeDate, endDate)   <= 0
            ),
          }));
        }
      } catch (err) {
        setError(err);
        toast.error(err.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, switched, timeMode, dispatch]);

  const filteredCrewRoutes = useMemo(
    () => filterCrewRoutes({ crewRoutes: crewRoutesData, filters, timeMode }),
    [crewRoutesData, filters, timeMode]
  );

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleCheckErrors = useCallback(() => {
    try {
      if (!filteredCrewRoutes?.length) {
        toast.warning("No crew routes available to check.");
        return;
      }

      const checks = [];
      const cabinCrewRanks = crewRanksData
        .filter(r => r.rankType === 'Cabin Crew')
        .map(r => r.rankCode);

      const INSTRUCTOR_LINE_TRAINING_CODES = paramsMap.get("INSTRUCTOR_LINE_TRAINING_CODES") || 'L, C, A, B, U';
      const INTERNAL_AUDIT_CODES           = paramsMap.get("INTERNAL_AUDIT_CODES")            || 'I, i';
      const UNPAID_CODES                   = paramsMap.get("UNPAID_CODES")                    || 'O, o';
      const nonDutyCodes = `${INTERNAL_AUDIT_CODES}, ${UNPAID_CODES}`;

      const getCrewComplement = (acType, flightReg, sector) => {
        const active = crewComplements.filter(cc => cc.isActive && cc.acTypeCode === acType);
        const byRegSector = active.find(cc => cc.acRegs?.includes(flightReg) && cc.sectors?.length > 0 && cc.sectors.includes(sector));
        if (byRegSector?.crewComplement?.length > 0) return byRegSector.crewComplement;
        const byReg  = active.find(cc => cc.acRegs?.includes(flightReg) && !cc.sectors?.length);
        if (byReg?.crewComplement?.length > 0) return byReg.crewComplement;
        const byType = active.find(cc => !cc.acRegs?.length);
        if (byType?.crewComplement?.length > 0) return byType.crewComplement;
        const regInfo = aircrafts.find(a => a.acReg === flightReg);
        if (regInfo?.crewComplement?.length > 0) return regInfo.crewComplement;
        const acInfo  = acTypes.find(a => a.acTypeCode === acType);
        if (acInfo?.crewComplement?.length > 0) return acInfo.crewComplement;
        return [];
      };

      filteredCrewRoutes.forEach(({ routeDate, routeDateLocalBase, routeNo, flightNo, crewRoute, acType, flightReg, sectors, routeDetails, crewMembers }) => {
        const formattedDate = format(new Date(timeMode === "UTC" ? routeDate : routeDateLocalBase), "dd/MM/yy");

        const checkGender    = checkType === '' || checkType === 'gender';
        const checkTraining  = checkType === '' || checkType === 'training';
        const checkTAT       = checkType === '' || checkType === 'tat';
        const checkCrewCount = checkType === '' || checkType === 'crewCount';

        let crewCount = 0, mlCount = 0, fmCount = 0;
        crewMembers.forEach(({ post, gender }) => {
          if (!cabinCrewRanks.includes(post)) return;
          if (gender === 'ML') mlCount++;
          if (gender === 'FM') fmCount++;
          crewCount++;
        });

        if (checkGender) {
          if (crewCount >= MIN_CREW_COUNT && fmCount <= minFemaleQuantity) {
            checks.push({ routeDate: formattedDate, routeNo, flightNo, crewRoute,
              error: mlCount >= fmCount
                ? `Too many male cabin crew: ${mlCount} ML vs ${fmCount} FL`
                : `Too many female cabin crew: ${mlCount} ML vs ${fmCount} FL`,
            });
          }
          if (crewCount >= MIN_CREW_COUNT && fmCount >= maxFemaleQuantity) {
            checks.push({ routeDate: formattedDate, routeNo, flightNo, crewRoute,
              error: `Too many female cabin crew: ${mlCount} ML vs ${fmCount} FL`,
            });
          }
        }

        const crewComplement = getCrewComplement(acType, flightReg, sectors);

        if (checkTraining && crewComplement.length > 0 &&
          crewMembers.length >= crewComplement.length &&
          crewComplement.every(({ rankCode }) => crewMembers.some(m => m.post === rankCode))
        ) {
          const trnDuties = crewMembers.map(m => m.trnDuty).filter(Boolean);
          const missingInfo = [];
          trnDuties.forEach(duty => {
            const trainee = TRAINER_TO_TRAINEE[duty];
            const trainer = TRAINEE_TO_TRAINER[duty];
            if (trainee && !trnDuties.includes(trainee))
              missingInfo.push(`Trainer (${duty}) missing Trainee (${trainee})`);
            if (trainer && !trnDuties.includes(trainer))
              missingInfo.push(`Trainee (${duty}) missing Trainer (${trainer})`);
          });
          missingInfo.forEach(msg =>
            checks.push({ routeDate: formattedDate, routeNo, flightNo, crewRoute, error: msg })
          );
        }

        if (checkTAT) {
          routeDetails.forEach(f => {
            if (f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT) {
              checks.push({ routeDate: formattedDate, routeNo, flightNo, crewRoute,
                error: `Flight No. ${f.flightNo} has TAT ${f.diffRegScheduleTat} mins`,
              });
            }
          });
        }

        if (acType && checkCrewCount && crewComplement.length > 0) {
          const totalComp  = crewComplement.reduce((s, { quantity }) => s + (quantity || 0), 0);
          const dutyCrew   = crewMembers.filter(m =>
            !m.pax && (!m.trnDuty ||
              !nonDutyCodes.includes(m.trnDuty) ||
              (m.post === 'CM' && !INSTRUCTOR_LINE_TRAINING_CODES.includes(m.trnDuty)))
          );
          if (dutyCrew.length > totalComp) {
            checks.push({ routeDate: formattedDate, routeNo, acType, flightNo, crewRoute,
              error: `Augmented complement: ${dutyCrew.length} > ${totalComp}`,
            });
          }
        }
      });

      if (checks.length > 0) {
        setChecksList(checks);
        setShowCheckResultDialog(true);
      } else {
        toast.success("No errors found.");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to check errors!");
    }
  }, [timeMode, paramsMap, crewRanksData, minCrewRouteTAT, filteredCrewRoutes, acTypes, aircrafts, crewComplements, checkType, minFemaleQuantity, maxFemaleQuantity]);

  const exportRows = useMemo(() => filteredCrewRoutes.map((route) => {
    const {
      crewMembers = [], routeDetails = [], crewComplementRequired,
      routeDateLocalBase,
      spickup, epickup, apickup, seod, eeod, aeod,
      fullPickup, fullSPickup, fullEPickup, fullAPickup, fullSEod,
      fullRpt, fullEtd, fullEta, fullAtd, fullAta, fullEod, fullEEod, fullAEod,
      spickupLocalBase, epickupLocalBase, apickupLocalBase, seodLocalBase, eeodLocalBase, aeodLocalBase,
      fullPickupLocalBase, fullSPickupLocalBase, fullEPickupLocalBase, fullSEodLocalBase, fullAPickupLocalBase,
      fullRptLocalBase, fullEtdLocalBase, fullEtaLocalBase, fullAtdLocalBase, fullAtaLocalBase,
      fullEodLocalBase, fullEEodLocalBase, fullAEodLocalBase,
      pickupLocalBase, rptLocalBase, etdLocalBase, etaLocalBase, atdLocalBase, ataLocalBase, eodLocalBase,
      ...rest
    } = route;
    return {
      ...rest,
      ...(crewComplementRequired?.length && { crewComplementRequired: fmtCrewComplement(crewComplementRequired) }),
      routeDetails: routeDetails.map(rd => {
        const dateStr = rd.flightDate ? format(new Date(rd.flightDate), "dd/MM/yyyy") : "";
        return `${rd.flightNo || ""} ${rd.flightReg || ""} ${dateStr} ${rd.etd || ""} ${rd.dep || ""} ${rd.arr || ""} ${rd.eta || ""} ${rd.pax || ""}`;
      }).join("\n"),
      crewMembers: crewMembers.map(cm =>
        `${cm.crewCode || ""} ${cm.crewName || ""} (${cm.post || ""} | ${cm.base || ""})`
      ).join("\n"),
    };
  }), [filteredCrewRoutes]);

  const handleViewClick = useCallback((route) => {
    setCrewRoute(route);
    setCrewList([...route.crewMembers].sort((a, b) => a.rankPriority - b.rankPriority));
    setOpenRouteCrewListDialog(true);
  }, []);

  /* ── Derived ───────────────────────────────────────────────────── */
  const { acTypeOptions, flightRegOptions, depOptions, arrOptions } = useMemo(() => ({
    acTypeOptions:    buildOptions(crewRoutesData, 'acType'),
    flightRegOptions: buildOptions(crewRoutesData, 'flightReg'),
    depOptions:       buildOptions(crewRoutesData, 'dep'),
    arrOptions:       buildOptions(crewRoutesData, 'arr'),
  }), [crewRoutesData]);

  const getAtd = useCallback((_, row) =>
    timeMode === "UTC" ? row.atd || row.etd || "" : row.atdLocalBase || row.etdLocalBase || "",
  [timeMode]);

  const getAta = useCallback((_, row) =>
    timeMode === "UTC" ? row.ata || row.eta || "" : row.ataLocalBase || row.etaLocalBase || "",
  [timeMode]);

  /* ── Columns ───────────────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      field: "no", headerName: "No", width: 50, filterable: false, sortable: false,
      renderCell: (p) => p.api.getAllRowIds().indexOf(p.id) + 1,
    },
    {
      field: 'routeDetails', headerName: 'Legs', width: isMobile ? 350 : 360,
      renderCell: (p) => {
        const hasWarning = p.row?.routeDetails?.some(f => f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT);
        return (
          <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden',
            animation: hasWarning ? 'glow 1.5s ease-in-out infinite' : 'none',
            '@keyframes glow': { '0%': { boxShadow: '0 0 0px orange' }, '50%': { boxShadow: '0 0 16px orange' }, '100%': { boxShadow: '0 0 0px orange' } },
          }}>
            {p.row?.routeDetails.map((f, i) => {
              const fullAtd   = timeMode === 'UTC' ? formatInTimeZone(f.fullAtd || f.fullEtd, "UTC", "ddMMMyy") : formatInTimeZone(f.fullAtdLocalBase || f.fullEtdLocalBase, "UTC", "ddMMMyy");
              const atd       = timeMode === 'UTC' ? (f?.atd || f?.etd) : (f?.atdLocalBase || f?.etdLocalBase);
              const ata       = timeMode === 'UTC' ? (f?.ata || f?.eta) : (f?.ataLocalBase || f?.etaLocalBase);
              const tatWarn   = f.diffRegScheduleTat && f.diffRegScheduleTat < minCrewRouteTAT;
              return (
                <Box key={i} display="flex" alignItems="center" gap={1} sx={{ color: tatWarn ? theme.palette.warning.main : 'inherit' }}>
                  <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>{f.flightNo}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 54 }}>{f.flightReg || f.acType}</Typography>
                  <Typography variant="body2" sx={{ width: 48 }}>{fullAtd}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{atd}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.dep}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.arr}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{ata}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'left', width: 8 }}>{f.pax}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'left', width: 8 }}>{f.trnDuty}</Typography>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'crewMembers', headerName: 'CrewMembers', width: isMobile ? 310 : 360,
      renderCell: (p) => {
        const sorted = [...p.row.crewMembers].sort((a, b) => a.rankPriority - b.rankPriority);
        return (
          <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
            {sorted.map((m, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}
                sx={{ color: m?.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark }}
              >
                <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>{m.crewCode}</Typography>
                <Typography variant="body2">{m.crewName}</Typography>
                <Typography variant="body2">({m.post} | {m.base}{m.trnDuty && ` | ${m.trnDuty}`})</Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: timeMode === 'UTC' ? "routeDate" : "routeDateLocalBase", headerName: "RouteDate", width: 100,
      valueFormatter: (p) => format(new Date(p), "dd/MM/yy"),
    },
    { field: "routeNo",   headerName: "RouteNo",   width: 88 },
    { field: "flightNo",  headerName: "FlightNo",  width: 220 },
    { field: "crewRoute", headerName: "CrewRoute", width: 260, cellClassName: "name-column--cell" },
    { field: "acType",    headerName: "ACType",    width: 88 },
    { field: "flightReg", headerName: "FlightReg", width: 100 },
    { field: "dep",       headerName: "Dep",       width: 88 },
    { field: "arr",       headerName: "Arr",       width: 88 },
    { field: timeMode === 'UTC' ? "rpt" : "rptLocalBase", headerName: "Rpt", width: 88 },
    { field: timeMode === 'UTC' ? "std" : "stdLocalBase", headerName: "Std", width: 88 },
    { field: timeMode === 'UTC' ? "sta" : "staLocalBase", headerName: "Sta", width: 88 },
    { field: timeMode === 'UTC' ? "etd" : "etdLocalBase", headerName: "Etd", width: 88 },
    { field: timeMode === 'UTC' ? "eta" : "etaLocalBase", headerName: "Eta", width: 88 },
    { field: "atdDisplay", headerName: "Atd", width: 88, valueGetter: getAtd },
    { field: "ataDisplay", headerName: "Ata", width: 88, valueGetter: getAta },
    {
      field: "eod", headerName: "Eod", width: 88,
      renderCell: ({ row }) => {
        const aeod = timeMode === "UTC" ? row.fullAEod || row.fullEEod : row.fullAEodLocalBase || row.fullEEodLocalBase;
        const seod = timeMode === "UTC" ? row.fullSEod : row.fullSEodLocalBase;
        let cellStyle = {};
        if (aeod && seod) {
          const diffMinutes = (new Date(aeod) - new Date(seod)) / 60000;
          if (diffMinutes > 15) {
            const aeodDate = new Date(aeod);
            const today    = new Date();
            const isSameDay = timeMode === "UTC"
              ? aeodDate.getUTCFullYear() === today.getUTCFullYear() && aeodDate.getUTCMonth() === today.getUTCMonth() && aeodDate.getUTCDate() === today.getUTCDate()
              : aeodDate.getFullYear() === today.getFullYear() && aeodDate.getMonth() === today.getMonth() && aeodDate.getDate() === today.getDate();
            const color = diffMinutes > DELAY_THRESHOLDS.HIGH.minutes   ? DELAY_THRESHOLDS.HIGH.color
                        : diffMinutes > DELAY_THRESHOLDS.MEDIUM.minutes ? DELAY_THRESHOLDS.MEDIUM.color
                        : DELAY_THRESHOLDS.LOW.color;
            cellStyle = isSameDay
              ? { color, animation: "blink 1s step-start 0s infinite", fontWeight: "bold", "@keyframes blink": { "60%": { opacity: 0.3 } } }
              : { color };
          }
        }
        return (
          <Typography height="100%" display="flex" alignItems="center" variant="body2" sx={cellStyle}>
            {aeod && formatInTimeZone(aeod, "UTC", "HH:mm")}
          </Typography>
        );
      },
    },
    { field: "elapsedHours",    headerName: "ElapsedHours",    width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "routeDays",       headerName: "RouteDays",       width: 100, align: 'right' },
    { field: "blockHours",      headerName: "BH",              width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "sectors",         headerName: "Sectors",         width: 100, align: 'right' },
    { field: "posHours",        headerName: "PosHours",        width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "posSectors",      headerName: "PosSectors",      width: 100, align: 'right' },
    { field: "dhdHours",        headerName: "DhdHours",        width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "dhdSectors",      headerName: "DhdSectors",      width: 100, align: 'right' },
    { field: "trainingHours",   headerName: "TrainingHours",   width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "trainingSectors", headerName: "TrainingSectors", width: 100, align: 'right' },
    { field: "perdiemMinutes",  headerName: "PerdiemMinutes",  width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(0) : 0 },
    { field: "cLounge",         headerName: "CLounge",         width: 100, align: 'right' },
    { field: "hotelDays",       headerName: "HotelDays",       width: 100, align: 'right' },
    { field: "paxCount",        headerName: "PaxCount",        width: 100, align: 'right' },
    { field: "crewCount",       headerName: "CrewCount",       width: 100, align: 'right' },
    { field: "fcCount",         headerName: "FCCount",         width: 100, align: 'right' },
    { field: "ccCount",         headerName: "CCCount",         width: 100, align: 'right' },
    ...crewRanksData
      .slice()
      .sort((a, b) => (a.rankPriority ?? 9999) - (b.rankPriority ?? 9999))
      .map(rank => ({ field: rank.rankCode, headerName: rank.rankCode, width: 80, align: 'right' })),
  ], [timeMode, theme, isMobile, minCrewRouteTAT, crewRanksData, getAtd, getAta]);

  const cols = useMemo(() => [
    {
      field: "no", headerName: "No", flex: 1, filterable: false, sortable: false,
      renderCell: (p) => p.api.getAllRowIds().indexOf(p.id) + 1,
    },
    {
      field: 'flights', headerName: 'Legs', flex: 8,
      renderCell: (p) => {
        const hasWarning = p.row?.flights?.some(f => f.fdp != null && f.maxFDP != null && (f.fdp - f.maxFDP) > -0.25);
        return (
          <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden',
            animation: hasWarning ? 'glow 1.5s ease-in-out infinite' : 'none',
            '@keyframes glow': { '0%': { boxShadow: '0 0 0px orange' }, '50%': { boxShadow: '0 0 16px orange' }, '100%': { boxShadow: '0 0 0px orange' } },
          }}>
            {p.row?.flights.map((f, i) => {
              const depDate  = formatInTimeZone(f.depDate, "UTC", "ddMMMyy");
              const atd      = timeMode === 'UTC' ? (f?.atd || f?.etd || f?.std) : (f?.atdLocalBase || f?.etdLocalBase || f?.stdLocalBase);
              const ata      = timeMode === 'UTC' ? (f?.ata || f?.eta || f?.sta) : (f?.ataLocalBase || f?.etaLocalBase || f?.staLocalBase);
              const overFdp  = f.fdp && f.maxFDP && f.fdp > f.maxFDP ? f.fdp - f.maxFDP : null;
              return (
                <Box key={i} display="flex" alignItems="center" gap={1} sx={{ color: overFdp ? theme.palette.warning.main : 'inherit' }}>
                  <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>{f.flightNo}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 54 }}>{f.flightReg || f.acType}</Typography>
                  <Typography variant="body2" sx={{ width: 48 }}>{depDate}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{atd}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.dep}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.arr}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{ata}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'left', width: 8 }}>{f?.pax || ''}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right', width: 28 }}>{f.fdp?.toFixed(2)}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right', width: 28 }}>{f.maxFDP?.toFixed(2)}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right', width: 28, color: overFdp ? 'error.main' : 'inherit', animation: overFdp ? `${blink} 2s infinite` : "none" }}>
                    {overFdp && overFdp.toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'crewMembers', headerName: 'CrewMembers', flex: 6,
      renderCell: (p) => {
        const sorted = [...p.row.crewMembers].sort((a, b) => a.rankPriority - b.rankPriority);
        return (
          <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
            {sorted.map((m, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}
                sx={{ color: m?.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark }}
              >
                <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>{m.crewCode}</Typography>
                <Typography variant="body2">{m.crewName}</Typography>
                <Typography variant="body2">({m.post} | {m.base}{m.trnDuty && ` | ${m.trnDuty}`})</Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: timeMode === 'UTC' ? "routeDate" : "routeDateLocalBase", headerName: "RouteDate", flex: 1.5,
      valueFormatter: (p) => format(new Date(p), "dd/MM/yy"),
    },
    { field: "routeNo", headerName: "RouteNo", flex: 1.5 },
  ], [timeMode, theme]);

  const actionColumn = useMemo(() => {
    if (!canView) return [];
    return [{
      field: "action", headerName: "Action", filterable: false, sortable: false, width: 85,
      renderCell: (p) => (
        <Box height="100%" display="flex" justifyContent="center" alignItems="center">
          <Tooltip title="View">
            <IconButton
              sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green.deep } }}
              onClick={() => handleViewClick(p.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }];
  }, [canView, theme, handleViewClick]);

  const hiddenColumns = useMemo(() => ({
    routeDate: false, routeDateLocalBase: false, routeNo: false, flightNo: false,
    crewRoute: false, acType: false, flightReg: false, dep: false, arr: false,
    rpt: false, rptLocalBase: false, std: false, stdLocalBase: false,
    sta: false, staLocalBase: false, etd: false, etdLocalBase: false,
    eta: false, etaLocalBase: false, atd: false, atdLocalBase: false,
    ata: false, ataLocalBase: false, atdDisplay: false, ataDisplay: false,
    blockHours: false, sectors: false, posHours: false, posSectors: false,
    dhdHours: false, dhdSectors: false, trainingHours: false, trainingSectors: false,
    elapsedHours: false, routeDays: false, perdiemMinutes: false,
    paxCount: false, crewCount: false, fcCount: false, ccCount: false,
    cLounge: false, hotelDays: false,
    ...Object.fromEntries(crewRanksData.map(r => [r.rankCode, false])),
  }), [crewRanksData]);

  const nonMobileHiddenColumns = useMemo(() => ({
    std: false, stdLocalBase: false, sta: false, staLocalBase: false,
    etd: false, etdLocalBase: false, eta: false, etaLocalBase: false,
    atd: false, atdLocalBase: false, ata: false, ataLocalBase: false,
    fcCount: false, ccCount: false,
    ...Object.fromEntries(crewRanksData.map(r => [r.rankCode, false])),
  }), [crewRanksData]);

  const columnsToShow = useMemo(
    () => [...(switched ? columns : cols), ...actionColumn],
    [switched, columns, cols, actionColumn]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Crew Routes"
          subtitle={loading ? "Loading..." : `List of ${filteredCrewRoutes?.length || 0} Routes`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                {checkType === 'gender' && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Minimum female cabin crew" arrow>
                      <span>
                        <NumberControl min={0} max={100} value={minFemaleQuantity} onChange={setMinFemaleQuantity} textFieldWidth={45} />
                      </span>
                    </Tooltip>
                    <Tooltip title="Maximum female cabin crew" arrow>
                      <span>
                        <NumberControl min={0} max={100} value={maxFemaleQuantity} onChange={setMaxFemaleQuantity} textFieldWidth={45} />
                      </span>
                    </Tooltip>
                  </Box>
                )}
                <TextField
                  select size="small" label="Check Type" value={checkType}
                  onChange={(e) => setCheckType(e.target.value)}
                  sx={{ minWidth: { xs: 100, md: 120 } }}
                >
                  {checkTypes.map(({ value, label }) => (
                    <MenuItem key={value || "none"} value={value}>{label}</MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {canCheck && (
              <Tooltip title="Check errors" arrow>
                <span>
                  <StyledButton onClick={handleCheckErrors}>
                    <Warning />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            <ExportButton
              data={exportRows}
              fileName="CrewRoutes"
              formats={['xlsx', 'csv', 'pdf']}
              onExport={(fmt) => logClientAction("EXPORT_REPORT", { fileName: "CrewRoutes", format: fmt, startDate, endDate })}
            />

            <FilterButton filters={filters} onClick={() => setIsFilterDialogOpen(true)} />

            {isAdmin && (
              <Tooltip title={switched ? "Check TAT" : "Check FDP"} arrow>
                <ToggleButton
                  size="small" value="switched" selected={switched}
                  onChange={() => setSwitched(prev => !prev)}
                >
                  {switched ? <ToggleOn /> : <ToggleOff />}
                </ToggleButton>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2}
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <RouteList 
                routes={filteredCrewRoutes} 
                onView={handleViewClick} 
              />
            ) : (
              <DataGridComponent
                rows={filteredCrewRoutes}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}
            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen && (
        <RouteFilterDialog
          open={isFilterDialogOpen}
          onClose={() => setIsFilterDialogOpen(false)}
          onApply={setFilters}
          acTypeOptions={acTypeOptions}
          flightRegOptions={flightRegOptions}
          depOptions={depOptions}
          arrOptions={arrOptions}
          currentFilters={filters}
        />
      )}
      {showCheckResultDialog && (
        <CheckResultDialog
          open={showCheckResultDialog}
          onClose={() => setShowCheckResultDialog(false)}
          title="Check"
          checksList={checksList}
          descriptionKey="error"
        />
      )}
      {openRouteCrewListDialog && (
        <RouteCrewListDialog
          open={openRouteCrewListDialog}
          onClose={() => { setOpenRouteCrewListDialog(false); setCrewList([]); }}
          crewRoute={crewRoute}
          crewList={crewList}
        />
      )}
    </Box>
  );
};

export default CrewRoutes;
